// components/Pages/MeasurementRequests/MeasurementRequestDetails.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMeasurementRequestDetails, deleteMeasurementRequest, scheduleMeasurementRequest, cancelMeasurementRequest, completeMeasurementRequest } from "../../../../utils/redux/measurementRequest/measurementActions";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";

const MeasurementRequestDetailsPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedRequest, isLoading } = useSelector(state => state.measurementRequest);

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleData, setScheduleData] = useState({
        scheduled_date: '',
        scheduled_time_slot: '',
        admin_notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(fetchMeasurementRequestDetails(id));
    }, [id, dispatch]);

    const handleDelete = async () => {
        if (!window.confirm("Delete this measurement request?")) return;
        const res = await dispatch(deleteMeasurementRequest(id));
        if (res?.success) {
            toast.success("Deleted successfully");
            navigate("/measurement-requests");
        }
    };

    const handleSchedule = async () => {
        if (!scheduleData.scheduled_date) {
            toast.error("Please select a date");
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await dispatch(scheduleMeasurementRequest(id, scheduleData));
            if (res?.success) {
                setShowScheduleModal(false);
                setScheduleData({ scheduled_date: '', scheduled_time_slot: '', admin_notes: '' });
                dispatch(fetchMeasurementRequestDetails(id));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = async () => {
        const reason = prompt("Enter cancellation reason:");
        if (!reason) return;

        const res = await dispatch(cancelMeasurementRequest(id, reason));
        if (res?.success) {
            dispatch(fetchMeasurementRequestDetails(id));
        }
    };

    const handleComplete = async () => {
        const notes = prompt("Enter completion notes:");
        const res = await dispatch(completeMeasurementRequest(id, notes));
        if (res?.success) {
            dispatch(fetchMeasurementRequestDetails(id));
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            'PENDING': { class: 'badge bg-warning text-dark', text: 'Pending' },
            'SCHEDULED': { class: 'badge bg-info text-white', text: 'Scheduled' },
            'COMPLETED': { class: 'badge bg-success text-white', text: 'Completed' },
            'CANCELLED': { class: 'badge bg-danger text-white', text: 'Cancelled' }
        };
        return badges[status] || { class: 'badge bg-secondary text-white', text: status };
    };

    const getRequestTypeLabel = (type) => {
        return type === 'MEASUREMENT' ? 'Measurement' : 'Appointment';
    };

    const getTimeSlotLabel = (slot) => {
        const slots = {
            'MORNING': 'Morning (9 AM - 12 PM)',
            'AFTERNOON': 'Afternoon (12 PM - 4 PM)',
            'EVENING': 'Evening (4 PM - 7 PM)'
        };
        return slots[slot] || slot || 'N/A';
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading || !selectedRequest) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading request details...</p>
            </div>
        );
    }

    const request = selectedRequest;
    const statusBadge = getStatusBadge(request.status);
    const canSchedule = request.status === 'PENDING';
    const canComplete = request.status === 'SCHEDULED';
    const canCancel = request.status === 'PENDING' || request.status === 'SCHEDULED';

    return (
        <>
            <div className="container-fluid px-4 py-4">
                {/* Breadcrumb */}
                <nav aria-label="breadcrumb" className="mb-4">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/measurement-requests" className="text-decoration-none">
                                Measurement Requests
                            </Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Request #{request._id.slice(-8)}
                        </li>
                    </ol>
                </nav>

                {/* Header Card */}
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                            <div>
                                <h3 className="mb-2">Request #{request._id.slice(-8)}</h3>
                                <div className="d-flex gap-2 flex-wrap">
                                    <span className={statusBadge.class}>{statusBadge.text}</span>
                                    <span className={`badge ${request.request_type === 'MEASUREMENT' ? 'bg-primary' : 'bg-secondary'}`}>
                                        {getRequestTypeLabel(request.request_type)}
                                    </span>
                                    <span className="badge bg-light text-dark">
                                        Requested: {formatDateTime(request.createdAt)}
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {canSchedule && (
                                    <button className="btn btn-primary" onClick={() => setShowScheduleModal(true)}>
                                        <i className="bi bi-calendar-plus me-1"></i> Schedule Measurement
                                    </button>
                                )}
                                {canComplete && (
                                    <button className="btn btn-success" onClick={handleComplete}>
                                        <i className="bi bi-check-circle me-1"></i> Complete
                                    </button>
                                )}
                                {canCancel && (
                                    <button className="btn btn-outline-warning" onClick={handleCancel}>
                                        <i className="bi bi-x-circle me-1"></i> Cancel
                                    </button>
                                )}
                                <button className="btn btn-outline-danger" onClick={handleDelete}>
                                    <i className="bi bi-trash me-1"></i> Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="row">
                    {/* Left Column */}
                    <div className="col-md-6">
                        {/* Request Information Card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">Request Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Request ID:</div>
                                    <div className="col-8"><code>{request._id}</code></div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Type:</div>
                                    <div className="col-8">{getRequestTypeLabel(request.request_type)}</div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Requested On:</div>
                                    <div className="col-8">{formatDateTime(request.createdAt)}</div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Requested By:</div>
                                    <div className="col-8">
                                        <strong>{request.requested_by?.user_name}</strong>
                                        <br />
                                        <small className="text-muted">Role: {request.requested_by?.user_role}</small>
                                        <br />
                                        <small className="text-muted">Email: {request.requested_by?.user_email}</small>
                                        <br />
                                        <small className="text-muted">Phone: {request.requested_by?.user_phone || 'N/A'}</small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Client Details Card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">Client Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Name:</div>
                                    <div className="col-8"><strong>{request.client_details?.name || 'N/A'}</strong></div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Email:</div>
                                    <div className="col-8">{request.client_details?.email || 'N/A'}</div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-4 text-muted">Phone:</div>
                                    <div className="col-8">{request.client_details?.phone || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info Card */}
                        {request.additional_info && (
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Additional Information</h5>
                                </div>
                                <div className="card-body">
                                    <p className="mb-0">{request.additional_info}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column */}
                    <div className="col-md-6">
                        {/* Address Details Card */}
                        {request.address_details?.address_line_1 && (
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Address Details</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-2">
                                        <strong>Address:</strong>
                                        <div className="mt-1 ps-3">
                                            <div>{request.address_details.address_line_1}</div>
                                            {request.address_details.address_line_2 && <div>{request.address_details.address_line_2}</div>}
                                            {request.address_details.landmark && <div>Landmark: {request.address_details.landmark}</div>}
                                            <div>
                                                {request.address_details.city && <span>{request.address_details.city}, </span>}
                                                {request.address_details.state && <span>{request.address_details.state}, </span>}
                                                {request.address_details.country && <span>{request.address_details.country}</span>}
                                                {request.address_details.pincode && <span> - {request.address_details.pincode}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Schedule Details Card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">Schedule Details</h5>
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-5 text-muted">Preferred Date:</div>
                                    <div className="col-7">{formatDate(request.preferred_date)}</div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-5 text-muted">Preferred Time:</div>
                                    <div className="col-7">{getTimeSlotLabel(request.preferred_time_slot)}</div>
                                </div>
                                {request.scheduled_date && (
                                    <>
                                        <hr />
                                        <div className="row mb-3">
                                            <div className="col-5 text-muted">Scheduled Date:</div>
                                            <div className="col-7"><strong>{formatDate(request.scheduled_date)}</strong></div>
                                        </div>
                                        <div className="row mb-3">
                                            <div className="col-5 text-muted">Scheduled Time:</div>
                                            <div className="col-7"><strong>{getTimeSlotLabel(request.scheduled_time_slot)}</strong></div>
                                        </div>
                                    </>
                                )}
                                {request.completed_at && (
                                    <div className="row mb-3">
                                        <div className="col-5 text-muted">Completed At:</div>
                                        <div className="col-7">{formatDateTime(request.completed_at)}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Admin Notes Card */}
                        {request.admin_notes && (
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Admin Notes</h5>
                                </div>
                                <div className="card-body">
                                    <p className="mb-0 text-muted">{request.admin_notes}</p>
                                </div>
                            </div>
                        )}

                        {/* Cancellation Reason Card */}
                        {request.cancelled_reason && (
                            <div className="card shadow-sm mb-4 border-danger">
                                <div className="card-header bg-danger text-white">
                                    <h5 className="mb-0">Cancellation Reason</h5>
                                </div>
                                <div className="card-body">
                                    <p className="mb-0">{request.cancelled_reason}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Project Context (if available) */}
                {(request.project_context?.name || request.project_context?.board_name) && (
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-light">
                            <h5 className="mb-0">Project Context</h5>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                {request.project_context?.name && (
                                    <div className="col-md-6 mb-2">
                                        <strong>Project Name:</strong> {request.project_context.name}
                                    </div>
                                )}
                                {request.project_context?.code && (
                                    <div className="col-md-6 mb-2">
                                        <strong>Project Code:</strong> {request.project_context.code}
                                    </div>
                                )}
                                {request.project_context?.board_name && (
                                    <div className="col-md-6 mb-2">
                                        <strong>Board Name:</strong> {request.project_context.board_name}
                                    </div>
                                )}
                                {request.project_context?.rooms_count && (
                                    <div className="col-md-6 mb-2">
                                        <strong>Rooms Count:</strong> {request.project_context.rooms_count}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Schedule Modal */}
            <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} size="md" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Schedule Measurement</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Schedule Date *</label>
                        <input
                            type="date"
                            className="form-control"
                            value={scheduleData.scheduled_date}
                            onChange={(e) => setScheduleData({ ...scheduleData, scheduled_date: e.target.value })}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Time Slot</label>
                        <select
                            className="form-select"
                            value={scheduleData.scheduled_time_slot}
                            onChange={(e) => setScheduleData({ ...scheduleData, scheduled_time_slot: e.target.value })}
                        >
                            <option value="">Select time slot</option>
                            <option value="MORNING">Morning (9 AM - 12 PM)</option>
                            <option value="AFTERNOON">Afternoon (12 PM - 4 PM)</option>
                            <option value="EVENING">Evening (4 PM - 7 PM)</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label fw-bold">Admin Notes</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            value={scheduleData.admin_notes}
                            onChange={(e) => setScheduleData({ ...scheduleData, admin_notes: e.target.value })}
                            placeholder="Internal notes for the team..."
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSchedule} disabled={isSubmitting}>
                        {isSubmitting ? 'Scheduling...' : 'Schedule'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MeasurementRequestDetailsPage;